/**
 * Página pública dos links dos clientes — `links.html`, servida em `/l/<apelido>` no site da
 * dash e em `/<apelido>` num domínio só de links (site próprio no Hosting).
 *
 * Fica FORA do app de propósito, como o portal: sem React, sem Firebase SDK, sem login.
 * Lê o documento pelo REST do Firestore (as regras só deixam ler link ATIVO, um por vez) e:
 *   - encurtador → soma a visita e redireciona na hora (ou «indisponível», se venceu);
 *   - página     → desenha a vitrine e soma visita/clique de cada bloco; recebe contatos.
 * A contagem usa `keepalive`, então sobrevive à troca de página. Nenhuma Cloud Function:
 * custo zero além das leituras e gravações do Firestore (cota gratuita).
 */
import { desenharVitrine } from './vitrine.js';
import { camposDoRest } from './firestoreRest.js';
import {
  COLECAO_LINKS, SUBCOLECAO_CONTAGEM, SUBCOLECAO_CONTATOS, REGEX_APELIDO, destinoSeguro,
  chavesDaVisita, chavesDoClique, REGEX_CHAVE_CONTAGEM, encurtadorVencido, redirecionamentoAtivo,
  resolverTema, deveContar, PARAM_EQUIPE, ehWhatsapp,
} from '../links/linksModelo.js';
import './vitrine.css';
import './linkPublico.css';

// Mesmo projeto e chave web do app (`src/firebase.js`). A chave web é pública por natureza;
// quem protege os dados são as regras.
const PROJETO = 'dashboard-messa';
const CHAVE_WEB = 'AIzaSyCXxEuUU68XuvIZuBSAjYLdyVmVX1VIjms';
const RAIZ_DOCS = `projects/${PROJETO}/databases/(default)/documents`;
const BASE = `https://firestore.googleapis.com/v1/${RAIZ_DOCS}`;

const raiz = document.getElementById('lk-root');

/**
 * Quem conta: gente de verdade, uma visita por aba (recarregar não soma de novo). Robô,
 * pré-visualização de link e a equipe (aberto pelo botão da dash, que põe `?equipe=1`, e o
 * navegador fica marcado) não somam.
 */
const CHAVE_EQUIPE = 'messa-links:equipe';
let equipe = false;
try {
  if (new RegExp(`[?&]${PARAM_EQUIPE}=1(&|$)`).test(location.search)) localStorage.setItem(CHAVE_EQUIPE, '1');
  equipe = localStorage.getItem(CHAVE_EQUIPE) === '1';
} catch { /* navegação privada: segue contando */ }
const CONTA = deveContar({
  agente: navigator.userAgent,
  busca: location.search,
  automatizado: navigator.webdriver === true,
  marcadoEquipe: equipe,
});

// O «?equipe=1» do botão «Abrir» da dash já foi lido e guardado: sai do endereço, para ninguém
// copiar e divulgar o link com ele (as visitas desse link deixariam de contar).
try {
  const u = new URL(location.href);
  if (u.searchParams.has(PARAM_EQUIPE)) {
    u.searchParams.delete(PARAM_EQUIPE);
    history.replaceState(history.state, '', u.pathname + u.search + u.hash);
  }
} catch { /* endereço continua como veio */ }

function primeiraVisitaDaAba(apelido) {
  try {
    const k = `messa-links:visto:${apelido}`;
    if (sessionStorage.getItem(k)) return false;
    sessionStorage.setItem(k, '1');
  } catch { /* sem sessionStorage: conta */ }
  return true;
}

function commit(writes) {
  return fetch(`${BASE}:commit?key=${CHAVE_WEB}`, {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ writes }),
  });
}

function contar(apelido, chaves) {
  if (!CONTA) return;
  try {
    const writes = chaves.filter((k) => REGEX_CHAVE_CONTAGEM.test(k)).map((k) => ({
      update: { name: `${RAIZ_DOCS}/${COLECAO_LINKS}/${apelido}/${SUBCOLECAO_CONTAGEM}/${k}`, fields: {} },
      updateMask: { fieldPaths: [] },
      updateTransforms: [{ fieldPath: 'n', increment: { integerValue: '1' } }],
    }));
    if (writes.length) commit(writes).catch(() => {});
  } catch { /* contagem nunca atrapalha a abertura */ }
}

function idAleatorio() {
  const a = new Uint8Array(12);
  crypto.getRandomValues(a);
  return Array.from(a, (x) => x.toString(16).padStart(2, '0')).join('');
}

async function enviarContato(apelido, bloco, dados) {
  const fields = {
    blocoId: { stringValue: String(bloco.id) },
    blocoRotulo: { stringValue: String(bloco.rotulo || '').slice(0, 80) },
  };
  for (const [k, v] of Object.entries(dados)) fields[k] = { stringValue: v };
  const r = await commit([{
    update: { name: `${RAIZ_DOCS}/${COLECAO_LINKS}/${apelido}/${SUBCOLECAO_CONTATOS}/${idAleatorio()}`, fields },
    updateTransforms: [{ fieldPath: 'criadoEm', setToServerValue: 'REQUEST_TIME' }],
    currentDocument: { exists: false },
  }]);
  if (!r.ok) throw new Error(`contato ${r.status}`);
}

function indisponivel() {
  document.title = 'Link indisponível';
  raiz.replaceChildren();
  const box = document.createElement('div');
  box.className = 'lk-aviso';
  const h = document.createElement('h1');
  h.textContent = 'Este link não está disponível';
  const p = document.createElement('p');
  p.textContent = 'Confira se o endereço foi digitado certo ou peça um link atualizado a quem enviou.';
  box.append(h, p);
  raiz.append(box);
}

function seguir(destino) {
  // Sem histórico: o «voltar» do celular não cai de novo neste redirecionamento.
  location.replace(destino);
  const a = document.getElementById('lk-seguir');
  if (a) { a.href = destino; a.hidden = false; }
}

function meta(nome, conteudo, atributo = 'name') {
  if (!conteudo) return;
  let m = document.querySelector(`meta[${atributo}="${nome}"]`);
  if (!m) {
    m = document.createElement('meta');
    m.setAttribute(atributo, nome);
    document.head.append(m);
  }
  m.setAttribute('content', conteudo);
}

/**
 * Conversão para os anúncios do cliente: toque no WhatsApp = «Contact» no Meta e
 * `clique_whatsapp` no GA4; formulário enviado = «Lead» e `generate_lead`. Visita da equipe,
 * robô ou navegador automatizado não dispara (mesma regra da contagem).
 */
function conversao(tipo) {
  if (!CONTA) return;
  try {
    if (typeof window.fbq === 'function') window.fbq('track', tipo === 'lead' ? 'Lead' : 'Contact');
    if (typeof window.gtag === 'function') window.gtag('event', tipo === 'lead' ? 'generate_lead' : 'clique_whatsapp', { transport_type: 'beacon' });
  } catch { /* rastreio nunca atrapalha o clique */ }
}

/** Pixel da Meta e Google Analytics 4 do cliente, só se configurados (ids validados na gravação). */
function rastreadores(r) {
  // A equipe abrindo a página (botão «Abrir» da dash) não entra no público nem nos números do cliente.
  if (!CONTA) return;
  if (r?.pixelMeta && /^\d{8,20}$/.test(r.pixelMeta)) {
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
    window.fbq('init', r.pixelMeta);
    window.fbq('track', 'PageView');
  }
  if (r?.ga4 && /^G-[A-Z0-9]{4,14}$/.test(r.ga4)) {
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${r.ga4}`;
    document.head.append(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', r.ga4);
  }
}

function apelidoDaUrl() {
  const m = location.pathname.match(/^\/(?:l\/)?([a-z0-9-]+)\/?$/);
  return m ? m[1] : '';
}

async function abrir() {
  const apelido = apelidoDaUrl();
  if (!REGEX_APELIDO.test(apelido)) return indisponivel();

  let doc;
  try {
    const r = await fetch(`${BASE}/${COLECAO_LINKS}/${apelido}?key=${CHAVE_WEB}`);
    if (!r.ok) return indisponivel();
    doc = camposDoRest((await r.json()).fields);
  } catch {
    return indisponivel();
  }
  if (!doc || doc.ativo !== true) return indisponivel();
  const ctx = { busca: location.search, referencia: document.referrer, agente: navigator.userAgent };

  if (doc.tipo === 'encurtador') {
    const destino = destinoSeguro(doc.destino);
    if (!destino || encurtadorVencido(doc)) return indisponivel();
    if (primeiraVisitaDaAba(apelido)) contar(apelido, chavesDaVisita(ctx));
    return seguir(destino);
  }

  const pagina = doc.pagina || {};
  const redireciona = redirecionamentoAtivo(pagina);
  if (redireciona) {
    if (primeiraVisitaDaAba(apelido)) contar(apelido, chavesDaVisita(ctx));
    return seguir(redireciona);
  }

  document.title = pagina.seo?.titulo || pagina.nome || 'Links';
  meta('description', pagina.seo?.descricao || pagina.bio || '');
  meta('og:title', document.title, 'property');
  meta('og:description', pagina.seo?.descricao || pagina.bio || '', 'property');
  if (pagina.foto) meta('og:image', pagina.foto, 'property');

  desenharVitrine(raiz, pagina, {
    aoClicar: (b) => {
      contar(apelido, chavesDoClique(b.id));
      if (ehWhatsapp(destinoSeguro(b.url))) conversao('whatsapp');
    },
    aoClicarRede: (s) => { if (ehWhatsapp(destinoSeguro(s.url))) conversao('whatsapp'); },
    aoEnviarContato: async (b, dados) => {
      await enviarContato(apelido, b, dados);
      conversao('lead');
    },
  });
  const cor = resolverTema(pagina).corBase;
  meta('theme-color', cor);
  document.body.style.background = cor;
  if (primeiraVisitaDaAba(apelido)) contar(apelido, chavesDaVisita(ctx));
  rastreadores(pagina.rastreio);
}

abrir();
